import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Clock,
  ArrowLeft,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABELS = {
  unassigned: "Unassigned",
  awaiting_acceptance: "Awaiting Acceptance",
  active_care: "Active Care",
  completed: "Completed",
};

const PRIORITY_LABELS = {
  routine: "Routine",
  time_sensitive: "Time-Sensitive",
  urgent_admin_review: "Urgent Admin Review",
};

function StageBadge({ stage }) {
  const styles = {
    unassigned: "bg-slate-100 text-slate-700 border-slate-200",
    awaiting_acceptance: "bg-amber-50 text-amber-800 border-amber-200",
    active_care: "bg-emerald-50 text-emerald-800 border-emerald-200",
    completed: "bg-slate-50 text-slate-500 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[stage] || styles.unassigned}`}>
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

function PriorityBadge({ priority }) {
  if (!priority || priority === "routine") return null;
  const styles = {
    time_sensitive: "bg-orange-50 text-orange-800 border-orange-200",
    urgent_admin_review: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[priority] || ""}`}>
      {PRIORITY_LABELS[priority] || priority}
    </span>
  );
}

const DECLINE_REASONS = [
  { v: "unavailable", l: "Unavailable" },
  { v: "scheduling_conflict", l: "Scheduling conflict" },
  { v: "outside_service_area", l: "Outside service area" },
  { v: "conflict_of_interest", l: "Conflict of interest" },
  { v: "other", l: "Other" },
];

const CONTACT_TYPES = [
  "Initial Call", "Return Call", "Phone Support", "Text Contact",
  "Email / Message Support", "Video Visit", "In-Person Visit", "Home Visit",
  "Hospital Visit", "Workplace Visit", "Follow-Up Visit", "Family Contact",
  "Prayer / Spiritual Support", "Funeral / Memorial Attendance", "Resource Provided",
  "Referral", "No Answer / Voicemail", "Other",
];

const AREAS = [
  "Grief & Loss", "Marriage & Relationships", "Family & Parenting",
  "Emotional Well-Being", "Work & Career", "Financial Well-Being",
  "Health & Illness", "Spiritual / Faith Support",
];

export default function ChaplainCareDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("unavailable");
  const [declineNote, setDeclineNote] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().slice(0, 5),
    contact_type: "Phone Support",
    follow_up_needed: false,
    follow_up_date: "",
    areas_of_support: [],
    note: "",
  });
  const [followUpDate, setFollowUpDate] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [priority, setPriority] = useState("routine");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/care-coordination/${id}`);
      setRec(r.data);
      setFollowUpDate(r.data.follow_up_date || "");
      setNextAction(r.data.next_action || "");
      setPriority(r.data.priority || "routine");
    } catch (e) {
      toast.error("Record not found or not authorized");
      navigate("/chaplain-portal/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const call = async (fn, label) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !rec) {
    return <p className="text-sm text-slate-500" data-testid="chap-detail-loading">Loading…</p>;
  }

  const stage = rec.care_stage;

  const toggleArea = (a) => {
    setContact((c) => {
      const has = c.areas_of_support.includes(a);
      const list = has ? c.areas_of_support.filter((x) => x !== a) : [...c.areas_of_support, a];
      return { ...c, areas_of_support: list.slice(0, 3) };
    });
  };

  return (
    <div data-testid="chap-care-detail">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        data-testid="chap-detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center gap-2">
          <StageBadge stage={stage} />
          <PriorityBadge priority={rec.priority} />
        </div>
        <h2 className="mt-3 font-heading text-2xl text-slate-900">
          {rec.first_name} {rec.last_name}
        </h2>
        <p className="text-sm text-slate-600 mt-1">{rec.company_name}</p>
        <p className="mt-3 text-xs text-slate-500">
          Received {new Date(rec.created_at).toLocaleString()} · Preferred contact: {rec.preferred_contact}
        </p>
        {rec.reason && (
          <p className="mt-4 text-sm text-slate-700 whitespace-pre-wrap">
            <span className="font-medium">Employee's reason:</span> {rec.reason}
          </p>
        )}
      </div>

      {/* Workflow actions */}
      {stage !== "completed" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Workflow</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {stage === "awaiting_acceptance" && (
              <>
                <button type="button" data-testid="chap-btn-accept" disabled={busy}
                  onClick={() => call(() => api.post(`/admin/care-coordination/${rec.id}/accept`), "Accepted")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  <CheckCircle2 className="w-4 h-4" /> Accept assignment
                </button>
                <button type="button" data-testid="chap-btn-decline-open" disabled={busy}
                  onClick={() => setShowDecline((s) => !s)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  <XCircle className="w-4 h-4" /> Decline / Request Reassignment
                </button>
              </>
            )}
            <button type="button" data-testid="chap-btn-escalate" disabled={busy}
              onClick={() => call(() => api.post(`/admin/care-coordination/${rec.id}/escalate`, { escalate: rec.priority !== "urgent_admin_review" }),
                rec.priority !== "urgent_admin_review" ? "Escalated for admin review" : "De-escalated")}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
              <AlertTriangle className="w-4 h-4" /> {rec.priority === "urgent_admin_review" ? "Remove escalation" : "Escalate for admin review"}
            </button>
            {(stage === "awaiting_acceptance" || stage === "active_care") && (
              <button type="button" data-testid="chap-btn-complete" disabled={busy}
                onClick={() => {
                  const resolution = rec.follow_up_date ? "follow_up_completed" : "no_follow_up_needed";
                  if (!window.confirm(resolution === "follow_up_completed"
                    ? "Confirm the follow-up has been completed and this record is fully closed?"
                    : "Confirm no further follow-up is needed and this record is closed?")) return;
                  call(() => api.post(`/admin/care-coordination/${rec.id}/complete`, { confirm: true, follow_up_resolution: resolution }), "Completed");
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
                <CheckCircle2 className="w-4 h-4" /> Complete / Close
              </button>
            )}
          </div>

          {showDecline && (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <p className="text-sm font-medium text-slate-700">Provide a short operational reason</p>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <select
                  data-testid="chap-decline-reason"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                >
                  {DECLINE_REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
                <button type="button" data-testid="chap-btn-decline-submit" disabled={busy}
                  onClick={() => call(async () => {
                    await api.post(`/admin/care-coordination/${rec.id}/decline`, { reason: declineReason, note: declineNote || undefined });
                    navigate("/chaplain-portal/dashboard", { replace: true });
                  }, "Declined — returned to admin queue")}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-700 text-white hover:bg-slate-900 disabled:opacity-50">
                  Confirm decline
                </button>
              </div>
              <input type="text" value={declineNote} onChange={(e) => setDeclineNote(e.target.value)}
                placeholder="Optional operational note"
                className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </div>
          )}
        </div>
      )}

      {/* Operational fields */}
      {stage !== "completed" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Operational fields</p>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <label className="text-sm">
              <span className="text-xs text-slate-500 font-medium">Priority</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}
                data-testid="chap-field-priority"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                <option value="routine">Routine</option>
                <option value="time_sensitive">Time-Sensitive</option>
                <option value="urgent_admin_review">Urgent Admin Review</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="text-xs text-slate-500 font-medium">Follow-up date</span>
              <input type="date" value={followUpDate || ""} onChange={(e) => setFollowUpDate(e.target.value)}
                data-testid="chap-field-followup"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </label>
            <label className="text-sm sm:col-span-3">
              <span className="text-xs text-slate-500 font-medium">Next action</span>
              <input type="text" value={nextAction} onChange={(e) => setNextAction(e.target.value)} maxLength={200}
                data-testid="chap-field-next-action"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            </label>
          </div>
          <button
            type="button"
            data-testid="chap-btn-save-fields"
            disabled={busy}
            onClick={() => call(() => api.patch(`/admin/care-coordination/${rec.id}/fields`, {
              priority, follow_up_date: followUpDate, next_action: nextAction,
            }), "Saved")}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B] disabled:opacity-50"
          >
            Save fields
          </button>
        </div>
      )}

      {/* Record a care activity */}
      {stage === "active_care" && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Record a contact</p>
            <button type="button" onClick={() => setShowContact((s) => !s)} data-testid="chap-btn-add-contact"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50">
              <Plus className="w-3 h-3" /> {showContact ? "Cancel" : "New contact"}
            </button>
          </div>
          {showContact && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-xs text-slate-500 font-medium">Date</span>
                <input type="date" value={contact.date} onChange={(e) => setContact({ ...contact, date: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="text-sm">
                <span className="text-xs text-slate-500 font-medium">Time</span>
                <input type="time" value={contact.time} onChange={(e) => setContact({ ...contact, time: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="text-xs text-slate-500 font-medium">Type of contact</span>
                <select value={contact.contact_type} onChange={(e) => setContact({ ...contact, contact_type: e.target.value })}
                  data-testid="chap-contact-type"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm">
                  {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <div className="sm:col-span-2">
                <span className="text-xs text-slate-500 font-medium">Broad support category (up to 3)</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AREAS.map((a) => (
                    <label key={a} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs cursor-pointer ${contact.areas_of_support.includes(a) ? "bg-[#4A6FA5] text-white border-[#4A6FA5]" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                      <input type="checkbox" className="sr-only"
                        checked={contact.areas_of_support.includes(a)}
                        onChange={() => toggleArea(a)} /> {a}
                    </label>
                  ))}
                </div>
              </div>
              <label className="text-sm sm:col-span-2 flex items-start gap-2">
                <input type="checkbox" checked={contact.follow_up_needed} onChange={(e) => setContact({ ...contact, follow_up_needed: e.target.checked })}
                  data-testid="chap-contact-followup-needed"
                  className="mt-1" /> Follow-up needed
              </label>
              {contact.follow_up_needed && (
                <label className="text-sm">
                  <span className="text-xs text-slate-500 font-medium">Follow-up date</span>
                  <input type="date" value={contact.follow_up_date} onChange={(e) => setContact({ ...contact, follow_up_date: e.target.value })}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                </label>
              )}
              <label className="text-sm sm:col-span-2">
                <span className="text-xs text-slate-500 font-medium">Operational note (kept confidential; not shared with employer)</span>
                <textarea rows={3} value={contact.note} onChange={(e) => setContact({ ...contact, note: e.target.value })}
                  maxLength={4000}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <div className="sm:col-span-2">
                <button
                  type="button"
                  data-testid="chap-btn-save-contact"
                  disabled={busy}
                  onClick={() => call(async () => {
                    await api.post(`/admin/support-requests/${rec.id}/activities`, {
                      date: contact.date,
                      time: contact.time,
                      staff_name: rec.assigned_to_name || "",
                      contact_type: contact.contact_type,
                      note: contact.note,
                      follow_up_needed: contact.follow_up_needed,
                      follow_up_date: contact.follow_up_needed ? contact.follow_up_date || null : null,
                      areas_of_support: contact.areas_of_support,
                    });
                    setShowContact(false);
                    setContact({ ...contact, note: "", areas_of_support: [] });
                  }, "Contact recorded")}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B] disabled:opacity-50"
                >
                  Save contact
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Existing activities */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Recorded contacts</p>
        {!rec.activities?.length ? (
          <p className="mt-2 text-sm text-slate-500">No contacts recorded yet.</p>
        ) : (
          <ul className="mt-2 divide-y divide-slate-100">
            {rec.activities.map((a) => (
              <li key={a.id} className="py-2 text-sm">
                <p className="text-slate-800">
                  <Calendar className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
                  {a.date} {a.time} · <span className="text-slate-600">{a.contact_type}</span>
                </p>
                {a.follow_up_needed && a.follow_up_date && (
                  <p className="text-xs text-slate-500 mt-0.5">Follow-up: {a.follow_up_date}</p>
                )}
                {a.areas_of_support?.length ? (
                  <p className="text-xs text-slate-500 mt-0.5">Areas: {a.areas_of_support.join(", ")}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Status history */}
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6">
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Activity history</p>
        {!rec.status_history?.length ? (
          <p className="mt-2 text-sm text-slate-500">No history entries yet.</p>
        ) : (
          <ol className="mt-2 space-y-2">
            {rec.status_history.slice().reverse().map((h, i) => (
              <li key={i} className="text-sm">
                <p className="text-slate-800">
                  <Clock className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
                  <span className="font-medium">{h.type}</span>
                  {h.from_stage && h.to_stage ? ` · ${h.from_stage} → ${h.to_stage}` : ""}
                </p>
                <p className="text-xs text-slate-500">
                  {new Date(h.at).toLocaleString()} · {h.by_name}
                  {h.reason ? ` · reason: ${h.reason}` : ""}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
